import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ArrowRight, Fingerprint, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isPasskeySupported, startPasskeyLogin } from '@/utils/passkey';
import { toast } from 'sonner';

export default function LoginPage() {
  const { login, setUser } = useAuth();
  const navigate = useNavigate();
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  useEffect(() => {
    setPasskeyAvailable(isPasskeySupported());
  }, []);

  const handlePasskeyLogin = async () => {
    setPasskeyLoading(true);
    try {
      const result = await startPasskeyLogin();
      if (result.verified && result.user) {
        setUser(result.user);
        toast.success('Signed in with Passkey');
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        toast.error(err.message || 'Passkey authentication failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-[#020617] px-4 py-8 sm:py-0">
      {/* Video background */}
      <div className="absolute inset-0 overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute min-w-full min-h-full object-cover opacity-30"
          style={{ filter: 'brightness(0.5)' }}
        >
          <source src="https://customer-assets.emergentagent.com/job_657df0ad-2496-4c2a-b42e-93cd0609284a/artifacts/b5se8ucw_b6de3232-5867-4ad3-adef-fc8fda6a7dd6.mp4" type="video/mp4" />
        </video>
      </div>
      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/70 to-[#020617]/50" />

      {/* Content */}
      <div className="relative z-10 max-w-md w-full">
        <div className="text-center mb-8 sm:mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_aviation-doc-center/artifacts/csy3ijni_4c140981-c1c1-451c-b7c5-b4dcb0e574e7.png"
            alt="Aviation Compliance"
            className="h-20 w-20 sm:h-28 sm:w-28 rounded-xl mb-4 sm:mb-6 object-cover shadow-lg shadow-black/30 mx-auto"
          />
          <h1
            data-testid="login-title"
            className="text-3xl sm:text-4xl font-bold text-slate-100 tracking-tight mb-2 sm:mb-3"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            Aviation Compliance
          </h1>
          <p className="text-slate-400 text-sm sm:text-base leading-relaxed px-4 sm:px-0">
            CASA Documentation Monitoring & Compliance System
          </p>
          <p className="text-slate-600 font-mono text-[10px] sm:text-xs tracking-wider uppercase mt-2 sm:mt-3">
            Part 135 &middot; Part 121 &middot; Part 138
          </p>
        </div>

        <div className="bg-[#0f172a] border border-slate-800 rounded-lg p-5 sm:p-6 shadow-xl shadow-black/20 space-y-3">
          {/* Passkey / Face ID sign-in */}
          {passkeyAvailable && (
            <>
              <Button
                data-testid="passkey-login-btn"
                onClick={handlePasskeyLogin}
                disabled={passkeyLoading}
                className="w-full h-12 sm:h-12 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-md transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 sm:gap-3"
              >
                {passkeyLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Fingerprint className="h-5 w-5" />
                )}
                Sign in with Face ID / Passkey
              </Button>
              <div className="flex items-center gap-3 my-1">
                <div className="flex-1 h-px bg-slate-800" />
                <span className="text-[10px] text-slate-600 font-mono uppercase tracking-wider">or</span>
                <div className="flex-1 h-px bg-slate-800" />
              </div>
            </>
          )}

          {/* Google sign-in */}
          <Button
            data-testid="google-login-btn"
            onClick={login}
            className="w-full h-12 sm:h-12 bg-[#003087] hover:bg-[#003087]/90 text-white text-sm font-medium rounded-md transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 sm:gap-3"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24">
              <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" opacity="0.8"/>
              <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" opacity="0.6"/>
              <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" opacity="0.4"/>
            </svg>
            Sign in with Google
            <ArrowRight className="h-4 w-4" />
          </Button>

          {passkeyAvailable && (
            <p className="text-[10px] text-slate-600 text-center mt-2 px-2">
              Set up Passkey after signing in via Google for one-tap Face ID login
            </p>
          )}
        </div>

        <p className="text-center text-[10px] sm:text-[11px] text-slate-600 mt-5 sm:mt-6 font-mono tracking-wide">
          Royal Flying Doctor Service of South East
        </p>
      </div>
    </div>
  );
}
